import React, { useState } from 'react';
import dayjs from 'dayjs';
import { LEAVE_ABBR } from '../constants';
import ImageZoomDialog from './ImageZoomDialog';

const OV = { position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const MO = { background: '#fff', borderRadius: 8, maxWidth: 540, width: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto' };
const DIV = '1px solid #e5e7eb';

const InfoBox = ({ style, children }) => (
  <div style={{ borderRadius: 6, padding: '10px 14px', ...style }}>{children}</div>
);

const KVRow = ({ label, value, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{label}</span>
    {children || <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{value}</span>}
  </div>
);

// ชั่วโมงทำงานจริงจากเวลาเข้า-ออก (นาที): เข้าก่อน 08:00 นับจาก 08:00, หักพักเที่ยง 60 นาที (ยกเว้นเข้าหลังเที่ยง/ออกก่อน 13:00), หักสาย, หักไม่เช็คเอาท์/ข้ามวัน
const computeHours = (checkin, attendanceRules) => {
  if (!checkin?.checkIn) return null;
  const ci = dayjs(checkin.checkIn);
  const ciH = ci.hour(), ciM = ci.minute();
  const ciMin = ciH * 60 + ciM;
  const workStartMin = 8 * 60, workEndMin = 17 * 60;
  const coMin = checkin.checkOut ? dayjs(checkin.checkOut).hour() * 60 + dayjs(checkin.checkOut).minute() : null;
  // checkOut วันเดียวกับ checkIn แต่เวลา (นาฬิกา) น้อยกว่า checkIn ก็ถือว่าข้ามวันเหมือนกัน
  // (เช่น ลืมเช็คเอาท์แล้วมาปิดงานเช้าวันถัดไป แต่ถูกเซฟวันที่ผิดเป็นวันเดียวกับ checkIn)
  const isNextDayOrNoCheckout = !checkin.checkOut ||
    dayjs(checkin.checkOut).format('YYYY-MM-DD') !== ci.format('YYYY-MM-DD') ||
    coMin < ciMin;
  const isLunchPeriodCheckin = ciH === 12;
  const effectiveStartMin = isLunchPeriodCheckin ? 13 * 60 : Math.max(ciMin, workStartMin);
  const rawEffectiveEndMin = isNextDayOrNoCheckout ? workEndMin : Math.min(coMin, workEndMin);
  const isAfterLunch = ciH >= 12;
  const isEarlyCheckout = !isAfterLunch && !isNextDayOrNoCheckout && coMin != null && coMin < 13 * 60;
  const effectiveEndMin = isEarlyCheckout ? Math.min(rawEffectiveEndMin, 12 * 60) : rawEffectiveEndMin;
  const grossMin = Math.max(0, effectiveEndMin - effectiveStartMin);
  const afterLunchMin = isAfterLunch || isEarlyCheckout ? grossMin : Math.max(0, grossMin - 60);
  const lateMin = Math.max(0, ciMin - workStartMin);
  const lateRule = (attendanceRules || []).find((r) => r.type === 'LATE_ARRIVAL' && r.enabled);
  const isLateDeducted = !isAfterLunch && lateRule && lateMin >= lateRule.threshold_minutes;
  const lateDeductMin = isLateDeducted ? lateRule.deduct_minutes : 0;
  const nextDayRule = (attendanceRules || []).find((r) => r.type === 'LATE_CHECKOUT_NEXT_DAY' && r.enabled);
  const noCoDeductMin = isNextDayOrNoCheckout && nextDayRule ? nextDayRule.deduct_minutes : 0;
  const finalMin = Math.max(0, afterLunchMin - lateDeductMin - noCoDeductMin);
  const finalHr = Math.round((finalMin / 60) * 100) / 100;
  return {
    workStartMin, isNextDayOrNoCheckout, isLunchPeriodCheckin, effectiveStartMin, effectiveEndMin,
    isEarlyCheckout, grossMin, isAfterLunch, isLateDeducted, lateDeductMin, noCoDeductMin,
    finalMin, finalHr,
  };
};

const DayDetailView = React.memo(function DayDetailView({
  open, onClose, dvData, attendanceRules, month, year,
  hrMgmt, onSkipLateDeduct, skipLateDvSaving, saveSkipLateDv,
}) {
  const [zoomSrc, setZoomSrc] = useState(null);
  if (!open) return null;
  const hoursCalc = dvData?.checkin ? computeHours(dvData.checkin, attendanceRules) : null;
  return (
    <div style={OV} onClick={onClose}>
      <div style={MO} onClick={(e) => e.stopPropagation()}>
        {/* Title */}
        <div style={{ padding: '14px 20px', borderBottom: DIV }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
            {dvData?.checkin ? 'รายละเอียดการลงเวลา' : 'รายละเอียดการลา'}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: 2 }}>{dvData?.empName}</div>
          {!dvData?.checkin && dvData?.dayNum && (
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              {`วันที่ ${String(dvData.dayNum).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`}
            </div>
          )}
        </div>

        <div style={{ padding: '16px 20px' }}>
          {/* Checkin detail */}
          {dvData?.checkin && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>โปรเจค</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', marginTop: 2 }}>{dvData.checkin.project_number || '—'}</div>
                  <div style={{ fontSize: '0.875rem', color: '#374151' }}>{dvData.checkin.project_name || '—'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>วันที่</div>
                  <div style={{ fontWeight: 700, marginTop: 2 }}>
                    {dvData.checkin.checkIn ? dayjs(dvData.checkin.checkIn).format('DD/MM/YYYY') : '—'}
                  </div>
                </div>
              </div>
              <hr style={{ border: 'none', borderTop: DIV, margin: '0 0 16px' }} />

              {/* Times */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>เวลาเข้า</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{dvData?.checkin?.checkInTime || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>เวลาออก</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{dvData?.checkin?.checkOutTime || '(ยังไม่เช็คเอาท์)'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>ชั่วโมงทำงาน</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#16a34a', lineHeight: 1.3 }}>
                    {hoursCalc ? `${hoursCalc.finalHr} ชม.` : '—'}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>Rate</div>
                  <span style={{ display: 'inline-block', marginTop: 4, padding: '2px 10px', background: '#7c3aed', color: '#fff', borderRadius: 14, fontWeight: 700, fontSize: '0.85rem' }}>
                    {dvData?.checkin?.effectiveRate || dvData?.checkin?.rate || 1}x
                  </span>
                </div>
              </div>
              <hr style={{ border: 'none', borderTop: DIV, margin: '0 0 16px' }} />

              {/* Photos + Calculation */}
              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                  {[
                    { label: 'Check In', photo: dvData?.checkin?.checkInPhoto || dvData?.checkin?.imageIn },
                    { label: 'Check Out', photo: dvData?.checkin?.checkOutPhoto || dvData?.checkin?.imageOut },
                  ].map(({ label, photo }) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      {photo ? (
                        <img
                          src={photo}
                          onClick={() => setZoomSrc(photo)}
                          title="คลิกเพื่อดูรูปขยาย"
                          style={{ width: 80, height: 80, borderRadius: 6, objectFit: 'cover', border: '1px solid #e5e7eb', display: 'block', cursor: 'zoom-in' }}
                          alt={label}
                        />
                      ) : (
                        <div style={{ width: 80, height: 80, background: '#f8fafc', borderRadius: 6, border: '1px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <span style={{ fontSize: 18, color: '#94a3b8' }}>📷</span>
                          <span style={{ fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>ไม่มีรูป</span>
                        </div>
                      )}
                      <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'block', marginTop: 6 }}>{label}</span>
                    </div>
                  ))}
                </div>

                {/* Calculation table */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: 10 }}>วิธีคำนวณ</div>
                  {(() => {
                    const checkin = dvData?.checkin;
                    if (!checkin?.checkIn || !hoursCalc) return null;
                    const ci = dayjs(checkin.checkIn);
                    const ciMin = ci.hour() * 60 + ci.minute();
                    const {
                      workStartMin, isNextDayOrNoCheckout, isLunchPeriodCheckin, effectiveStartMin,
                      effectiveEndMin, isEarlyCheckout, grossMin, isAfterLunch, isLateDeducted,
                      lateDeductMin, noCoDeductMin, finalMin, finalHr,
                    } = hoursCalc;
                    const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
                    const startNote = isLunchPeriodCheckin ? `(เช็คอิน ${fmtMin(ciMin)} → เริ่มนับ 13:00)` : effectiveStartMin > workStartMin ? '(หลัง 08:00)' : '';
                    const rows = [
                      { label: 'เริ่มนับ', value: fmtMin(effectiveStartMin), note: startNote },
                      { label: 'สิ้นสุด', value: fmtMin(effectiveEndMin), note: isNextDayOrNoCheckout ? '(cap 17:00)' : isEarlyCheckout ? '(cap 12:00)' : '' },
                      { label: 'รวม', value: `${(grossMin / 60).toFixed(2)} ชม. (${grossMin} นาที)` },
                      isAfterLunch ? { label: 'พักเที่ยง', value: 'ไม่หัก (เข้างานหลัง 12:00)', color: '#16a34a' }
                        : isEarlyCheckout ? { label: 'พักเที่ยง', value: 'ไม่หัก (ออกก่อน 13:00)', color: '#16a34a' }
                        : { label: 'หักพักเที่ยง', value: '−1.00 ชม. (−60 นาที)', color: '#6b7280' },
                      ...(isLateDeducted ? [{ label: 'หักสาย', value: `−${(lateDeductMin / 60).toFixed(2)} ชม. (−${lateDeductMin} นาที)`, color: '#f97316' }] : []),
                      ...(noCoDeductMin > 0 ? [{ label: !checkin.checkOut ? 'ไม่เช็คเอาท์' : 'เช็คออกข้ามวัน', value: `−${(noCoDeductMin / 60).toFixed(2)} ชม. (−${noCoDeductMin} นาที)`, color: '#ef4444' }] : []),
                      { label: 'ชม.ที่ใช้', value: `${finalHr} ชม. (${finalMin} นาที)`, bold: !dvData?.leaveAutoH },
                      ...(dvData?.leaveAutoH > 0 ? [
                        { label: '+ลาครึ่งวัน', value: `+${dvData.leaveAutoH} ชม.`, color: '#15803d' },
                        { label: 'รวมทั้งหมด', value: `${Math.round((finalHr + dvData.leaveAutoH) * 100) / 100} ชม.`, bold: true, color: '#15803d' },
                      ] : []),
                    ];
                    return (
                      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i} style={row.bold ? { borderTop: DIV } : {}}>
                              <td style={{ padding: '3px 6px 3px 0', fontSize: '0.72rem', color: row.color || (row.bold ? 'inherit' : '#6b7280'), fontWeight: row.bold ? 700 : 400, whiteSpace: 'nowrap' }}>{row.label}</td>
                              <td style={{ padding: '3px 0', fontSize: '0.72rem', fontWeight: row.bold ? 700 : 400, color: row.color || 'inherit', textAlign: 'right', whiteSpace: 'nowrap' }}>{row.value}</td>
                              {row.note && <td style={{ padding: '3px 0 3px 4px', fontSize: '0.65rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{row.note}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </>
          )}

          {/* Leave info */}
          {dvData?.leaveAutoH > 0 && (() => {
            const lv = dvData.leave;
            const periodLabel = lv?.period === 'FULL_DAY' ? 'เต็มวัน' : lv?.period === 'MORNING' ? 'ครึ่งวันเช้า' : 'ครึ่งวันบ่าย';
            const lvName = LEAVE_ABBR[lv?.leave_type] || lv?.leave_type || 'ลา';
            return (
              <>
                <hr style={{ border: 'none', borderTop: DIV, margin: '16px 0 12px' }} />
                <InfoBox style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#15803d', marginBottom: 8 }}>📋 การลา — ลา{lvName} ({periodLabel})</div>
                  <KVRow label="ชั่วโมงที่ได้จากการลา"><span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#15803d' }}>+{dvData.leaveAutoH} ชม.</span></KVRow>
                  {lv?.reason && <KVRow label="เหตุผล"><span style={{ fontSize: '0.72rem', color: '#374151', textAlign: 'right', maxWidth: 180 }}>{lv.reason}</span></KVRow>}
                </InfoBox>
                {!dvData?.checkin && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: 8 }}>วิธีคำนวณ</div>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <tbody>
                        <tr><td style={{ padding: '3px 6px 3px 0', fontSize: '0.72rem', color: '#15803d' }}>+ชั่วโมงการลา</td><td style={{ padding: '3px 0', fontSize: '0.72rem', color: '#15803d', textAlign: 'right' }}>+{dvData.leaveAutoH} ชม.</td></tr>
                        <tr style={{ borderTop: DIV }}><td style={{ padding: '3px 6px 3px 0', fontSize: '0.72rem', color: '#15803d', fontWeight: 700 }}>รวม</td><td style={{ padding: '3px 0', fontSize: '0.72rem', color: '#15803d', fontWeight: 700, textAlign: 'right' }}>{dvData.leaveAutoH} ชม.</td></tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}

          {/* HR override / manual */}
          {(dvData?.manualEntry || dvData?.checkin?.salary_override?.amount != null) && (
            <>
              <hr style={{ border: 'none', borderTop: DIV, margin: '16px 0 12px' }} />
              {dvData?.manualEntry && (() => {
                const mn = dvData.manualEntry;
                const effAmt = mn.amount != null ? Number(mn.amount) : (Number(mn.hours || 0) / 8) * Number(mn.salary_per_day || 0);
                return (
                  <InfoBox style={{ background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 8 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1d4ed8', marginBottom: 8 }}>✎ เพิ่มชั่วโมงโดย HR</div>
                    <KVRow label="โดย"><span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b' }}>{mn.edited_by || '—'}</span></KVRow>
                    <KVRow label="เพิ่มชั่วโมง"><span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1d4ed8' }}>{mn.hours} ชม.</span></KVRow>
                    <KVRow label="ค่าแรง">
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1d4ed8' }}>
                        ฿{effAmt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        <span style={{ color: '#93c5fd', fontWeight: 400, marginLeft: 4, fontSize: '0.68rem' }}>{mn.amount != null ? '(กำหนดเอง)' : '(อัตโนมัติ)'}</span>
                      </span>
                    </KVRow>
                  </InfoBox>
                );
              })()}
              {(() => {
                if (dvData?.checkin?.salary_override?.amount == null) return false;
                if (!dvData?.manualEntry) return true;
                const mn = dvData.manualEntry;
                const effAmt = mn.amount != null ? mn.amount : (mn.hours / 8) * (mn.salary_per_day || 0);
                return Math.abs(dvData.checkin.salary_override.amount - effAmt) > 0.01;
              })() && (
                <InfoBox style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b45309', marginBottom: 8 }}>✎ แก้ไขค่าแรงโดย HR</div>
                  <KVRow label="โดย"><span style={{ fontSize: '0.72rem', fontWeight: 600 }}>{dvData.checkin.salary_override.edited_by || '—'}</span></KVRow>
                  {dvData.checkin.salary_override.edited_at && (
                    <KVRow label="เมื่อ"><span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{dayjs(dvData.checkin.salary_override.edited_at).format('DD/MM/YY HH:mm')}</span></KVRow>
                  )}
                  <KVRow label="ค่าแรง">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.72rem', color: '#9ca3af', textDecoration: 'line-through' }}>
                        ฿{dvData.autoAmt != null ? Number(dvData.autoAmt).toLocaleString('th-TH', { minimumFractionDigits: 2 }) : '—'}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>→</span>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b45309' }}>
                        ฿{Number(dvData.checkin.salary_override.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </KVRow>
                </InfoBox>
              )}
            </>
          )}
        </div>

        {/* Skip late deduct */}
        {hrMgmt && onSkipLateDeduct && dvData?.checkin && (
          <div style={{ padding: '0 20px 12px' }}>
            <hr style={{ border: 'none', borderTop: DIV, margin: '0 0 12px' }} />
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: dvData.checkin.skip_late_deduct ? 'rgba(249,115,22,0.07)' : 'transparent',
              padding: '8px 12px', borderRadius: 4,
              border: `1px solid ${dvData.checkin.skip_late_deduct ? 'rgba(249,115,22,0.35)' : '#e5e7eb'}`,
            }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: dvData.checkin.skip_late_deduct ? '#ea580c' : '#6b7280' }}>
                  {dvData.checkin.skip_late_deduct ? '✓ ยกเว้นหักสายแล้ว' : 'ยกเว้นหักสาย'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {dvData.checkin.skip_late_deduct ? 'คลิก toggle เพื่อกลับมาหักสายตามปกติ' : 'เปิดเพื่อไม่หักเงินมาสายวันนี้'}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: skipLateDvSaving ? 'not-allowed' : 'pointer' }}>
                <input type="checkbox" checked={!!dvData.checkin.skip_late_deduct}
                  onChange={(e) => saveSkipLateDv(e.target.checked)} disabled={skipLateDvSaving} />
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>เปิด/ปิด</span>
              </label>
            </div>
          </div>
        )}

        <div style={{ padding: '10px 20px 16px', borderTop: DIV, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '6px 20px', borderRadius: 4, border: 'none', backgroundColor: '#6366f1', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
            ปิด
          </button>
        </div>
      </div>

      <ImageZoomDialog src={zoomSrc} alt="รูปลงเวลา" onClose={() => setZoomSrc(null)} />
    </div>
  );
});

export default DayDetailView;
